import { useEffect, useState } from "react";
import { FloppyDisk, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import NotificationPrefsEditor, { mergeNotifPrefs } from "@/components/NotificationPrefsEditor";

const ROLE_LABEL = { owner: "Owner", report_writer: "Report Writer", field_executive: "Field Executive", accountant: "Accountant" };

export default function TeamNotifications() {
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState(false);
  const member = members.find((m) => m.id === memberId);

  useEffect(() => {
    api.get("/team").then(({ data }) => {
      const list = (Array.isArray(data) ? data : []).filter((m) => m.is_active !== false);
      setMembers(list);
      if (list[0]) setMemberId(list[0].id);
    }).catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  }, []);

  const load = () => {
    if (!memberId) return;
    setForm(null); setLoadErr(false);
    api.get(`/team/${memberId}/notifications`)
      .then((r) => setForm(mergeNotifPrefs(r.data)))
      .catch((e) => { setLoadErr(true); toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load preferences"); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [memberId]);

  const save = async () => {
    setBusy(true);
    try { await api.put(`/team/${memberId}/notifications`, form); toast.success(`Saved for ${member?.name || "member"}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="team-notifications-page">
      <PageHeader overline="04 · Team Management" title="Team Notification Preferences" subtitle="Set what alerts each team member receives, and how" />

      <Card className="p-6 border-border">
        <SectionTitle icon={UsersThree}>Team member</SectionTitle>
        <Select value={memberId} onValueChange={setMemberId}>
          <SelectTrigger className="mt-1" data-testid="tn-member-select"><SelectValue placeholder="Choose a team member" /></SelectTrigger>
          <SelectContent>
            {members.map((m) => (
              <SelectItem key={m.id} value={m.id} data-testid={`tn-member-${m.id}`}>
                {m.name} · {ROLE_LABEL[m.role] || m.role}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-2">You are editing preferences on behalf of this member. They can view (but not change) these in their own account.</p>
      </Card>

      {loadErr ? (
        <div className="space-y-3" data-testid="tn-load-error"><p className="text-sm text-destructive">Couldn't load this member's preferences. Saving is disabled until it loads correctly.</p><Button variant="outline" className="rounded-full" onClick={load}>Retry</Button></div>
      ) : !form ? (
        <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading preferences…</div>
      ) : (
        <>
          <NotificationPrefsEditor value={form} onChange={setForm} role={member?.role || "field_executive"} />
          <Button onClick={save} disabled={busy || !memberId} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="tn-save-btn">
            <FloppyDisk size={16} className="mr-1" /> {busy ? "Saving…" : `Save for ${member?.name || "member"}`}
          </Button>
        </>
      )}
    </div>
  );
}
